import type { Attachment } from '@nexo/types';
import { cn } from '@nexo/ui';
import { ChevronLeft, ChevronRight, Download, ExternalLink, X } from 'lucide-react';
import { useEffect, useRef, type ReactNode } from 'react';
import { formatBytes } from '../../lib/format';
import { isDesktop, openExternal } from '../../lib/platform';
import { FileIcon } from '../files/FileIcon';
import { ImageZoom } from './ImageZoom';
import { useViewer, viewer } from './viewerStore';
import { useTranslation } from 'react-i18next';

/** Full-screen viewer for images, PDF, video, audio and text, inside the app. */
export function FileViewer() {
  const state = useViewer();
  const { t } = useTranslation();
  const ref = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (state && !dialog.open) dialog.showModal();
    if (!state && dialog.open) dialog.close();
  }, [state]);

  useEffect(() => {
    if (!state) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'ArrowRight') viewer.go(1);
      if (event.key === 'ArrowLeft') viewer.go(-1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [state]);

  const file = state?.files[state.index];
  const many = (state?.files.length ?? 0) > 1;

  return (
    <dialog
      ref={ref}
      aria-label={file ? t('Vista previa de {{name}}', { name: file.name }) : t('Vista previa')}
      onClose={() => viewer.close()}
      className="m-0 h-dvh max-h-none w-dvw max-w-none bg-transparent p-0 text-white backdrop:bg-black/85 backdrop:backdrop-blur-sm"
    >
      {state && file && (
        <div
          className="flex h-full flex-col"
          onClick={(e) => e.target === e.currentTarget && viewer.close()}
        >
          <header className="flex h-14 shrink-0 items-center gap-3 px-4">
            <FileIcon
              contentType={file.contentType}
              name={file.name}
              className="size-5 shrink-0 text-white/70"
            />
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium">{file.name}</p>
              <p className="text-xs text-white/60">
                {formatBytes(file.size)}
                {many &&
                  t(' · {{index}} de {{total}}', {
                    index: state.index + 1,
                    total: state.files.length,
                  })}
              </p>
            </div>
            <ViewerButton label={t('Descargar')} onClick={() => download(file)}>
              <Download />
            </ViewerButton>
            <ViewerButton
              label={t('Abrir en otra pestaña')}
              onClick={() => void openExternal(file.previewUrl ?? file.url)}
            >
              <ExternalLink />
            </ViewerButton>
            <ViewerButton label={t('Cerrar (Esc)')} onClick={() => viewer.close()} autoFocus>
              <X />
            </ViewerButton>
          </header>

          <div className="relative flex min-h-0 flex-1 items-center justify-center px-4 pb-4 sm:px-16">
            {many && (
              <NavButton side="left" onClick={() => viewer.go(-1)}>
                <ChevronLeft />
              </NavButton>
            )}
            <Preview key={file.id} file={file} />
            {many && (
              <NavButton side="right" onClick={() => viewer.go(1)}>
                <ChevronRight />
              </NavButton>
            )}
          </div>

          {many && (
            <div className="flex shrink-0 justify-center gap-2 overflow-x-auto px-4 pb-4">
              {state.files.map((f, i) => (
                <button
                  key={f.id}
                  type="button"
                  aria-label={f.name}
                  onClick={() => viewer.open(state.files, i)}
                  className={cn(
                    'flex size-12 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-white/10 ring-2 transition',
                    i === state.index
                      ? 'ring-white'
                      : 'ring-transparent opacity-60 hover:opacity-100',
                  )}
                >
                  {f.isImage ? (
                    <img src={f.url} alt="" className="size-full object-cover" />
                  ) : (
                    <FileIcon
                      contentType={f.contentType}
                      name={f.name}
                      className="size-5 text-white/80"
                    />
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </dialog>
  );
}

function Preview({ file }: { file: Attachment }) {
  const { t } = useTranslation();
  const src = file.previewUrl;

  if (file.isImage && src) return <ImageZoom src={src} alt={file.name} />;
  if (src && file.contentType.startsWith('video/')) {
    return (
      <video
        src={src}
        controls
        autoPlay
        className="max-h-full max-w-full rounded-lg bg-black shadow-2xl"
      />
    );
  }
  if (src && file.contentType.startsWith('audio/')) {
    return (
      <div className="w-full max-w-md rounded-2xl bg-white/10 p-6 text-center">
        <FileIcon
          contentType={file.contentType}
          name={file.name}
          className="mx-auto mb-4 size-10 text-white/80"
        />
        <audio src={src} controls autoPlay className="w-full" />
      </div>
    );
  }
  if (src) {
    // PDF and plain text render natively in the browser/webview.
    return (
      <iframe
        src={src}
        title={file.name}
        className="h-full w-full max-w-5xl rounded-lg bg-white shadow-2xl"
      />
    );
  }
  return (
    <div className="max-w-sm rounded-2xl bg-white/10 p-8 text-center">
      <FileIcon
        contentType={file.contentType}
        name={file.name}
        className="mx-auto mb-4 size-12 text-white/80"
      />
      <p className="font-medium">{file.name}</p>
      <p className="mt-1 text-sm text-white/60">
        {t('No se puede previsualizar este tipo de archivo.')}
      </p>
      <button
        type="button"
        onClick={() => download(file)}
        className="mt-5 inline-flex items-center gap-2 rounded-lg bg-white px-4 py-2 text-sm font-medium text-black hover:bg-white/90"
      >
        <Download className="size-4" />{' '}
        {t('Descargar ({{size}})', { size: formatBytes(file.size) })}
      </button>
    </div>
  );
}

function download(file: Attachment) {
  // `url` is signed with Content-Disposition: attachment for non-images.
  if (isDesktop) {
    void openExternal(file.url);
    return;
  }
  const a = document.createElement('a');
  a.href = file.url;
  a.download = file.name;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
}

function ViewerButton({
  label,
  onClick,
  children,
  autoFocus,
}: {
  label: string;
  onClick: () => void;
  children: ReactNode;
  autoFocus?: boolean;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      autoFocus={autoFocus}
      onClick={onClick}
      className="inline-flex size-9 items-center justify-center rounded-lg text-white/80 hover:bg-white/15 hover:text-white [&_svg]:size-[18px]"
    >
      {children}
    </button>
  );
}

function NavButton({
  side,
  onClick,
  children,
}: {
  side: 'left' | 'right';
  onClick: () => void;
  children: ReactNode;
}) {
  const { t } = useTranslation();
  return (
    <button
      type="button"
      aria-label={side === 'left' ? t('Anterior (←)') : t('Siguiente (→)')}
      onClick={onClick}
      className={cn(
        'absolute top-1/2 z-10 flex size-11 -translate-y-1/2 items-center justify-center rounded-full bg-black/50 text-white hover:bg-black/70 [&_svg]:size-6',
        side === 'left' ? 'left-3' : 'right-3',
      )}
    >
      {children}
    </button>
  );
}
